/**
 * /arena/dashboard — Gym Owner Dashboard (mobile-first 5 tabs).
 * Tabs: Overview / Members / Trainers / Analytics / Settings.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, MessageCircle, AlertTriangle, TrendingUp, Trophy, Plus, Copy, Printer, Download } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import { ArenaShield } from "../../components/arena/ArenaAtoms";
import RoleSwitcher from "../../components/common/RoleSwitcher";
import { toast } from "sonner";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "members", label: "Members" },
  { id: "trainers", label: "Trainers" },
  { id: "analytics", label: "Analytics" },
  { id: "settings", label: "Settings" },
];

export default function ArenaDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  return (
    <div className="min-h-screen pb-24" style={{ background: "var(--bg-base, #ECEAE6)" }} data-testid="arena-dashboard">
      <header style={{ position: "sticky", top: 0, zIndex: 10, background: "var(--bg-elevated, #F0EEEA)", borderBottom: "1px solid var(--border, rgba(12,12,11,0.09))" }}>
        <div className="max-w-md mx-auto flex items-center px-4 py-3 gap-2">
          <button onClick={() => navigate(-1)} aria-label="Back" style={{ background: "transparent", border: "none" }}><ChevronLeft size={22} /></button>
          <ArenaShield />
          <span style={{ fontFamily: "Barlow Condensed", fontSize: 22, fontWeight: 800 }}>Arena Dashboard</span>
          <div style={{ flex: 1 }} />
          <RoleSwitcher variant="light" />
        </div>
      </header>

      <main className="max-w-md mx-auto px-5 pt-4">
        {tab === "overview" && <OverviewTab />}
        {tab === "members" && <MembersTab />}
        {tab === "trainers" && <TrainersTab />}
        {tab === "analytics" && <AnalyticsTab />}
        {tab === "settings" && <SettingsTab />}
      </main>

      <nav style={{ position: "fixed", left: 0, right: 0, bottom: 0, background: "white", borderTop: "1px solid var(--border, rgba(12,12,11,0.09))", paddingBottom: "env(safe-area-inset-bottom, 0)" }}>
        <div className="max-w-md mx-auto flex items-center justify-around" style={{ height: 56 }}>
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`dash-tab-${t.id}`}
              style={{
                flex: 1, height: "100%", background: "transparent", border: "none",
                fontFamily: "Barlow", fontSize: 11, fontWeight: 700,
                color: tab === t.id ? "var(--text-primary, #0C0C0B)" : "var(--text-tertiary, #8A8884)",
                borderTop: tab === t.id ? "2px solid #F5C518" : "2px solid transparent",
              }}>
              {t.label}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}

function OverviewTab() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const load = useCallback(() => apiClient.get("/arena/dashboard/overview").then((r) => setData(r.data)).catch(() => setData({ tiles: {}, recent_checkins: [], at_risk: [] })), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div>Loading…</div>;
  const t = data.tiles || {};
  const sendWA = (member) => {
    const text = `Hey ${member.name?.split(" ")[0]}, ${data.gym?.gym_name || "your gym"} misses you! Drop by today — even 30 minutes counts. 💪`;
    const url = member.phone ? `https://wa.me/${member.phone.replace(/\D/g, "")}?text=${encodeURIComponent(text)}` : `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };
  return (
    <div className="space-y-4">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }} data-testid="dash-tiles">
        <Tile label="Members" value={t.members_total || 0} />
        <Tile label="Today" value={t.today_checkins || 0} accent={t.today_checkins > 0} />
        <Tile label="At Risk" value={t.at_risk_count || 0} danger={t.at_risk_count > 0} />
        <Tile label={`This Month`} value={`₹${(t.revenue_month || 0).toLocaleString("en-IN")}`} />
      </div>

      {data.at_risk?.length > 0 && (
        <section style={{ padding: 14, background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.30)", borderLeft: "3px solid #EF4444", borderRadius: 12 }} data-testid="at-risk-section">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <AlertTriangle size={14} style={{ color: "#EF4444" }} />
            <strong style={{ fontFamily: "Barlow Condensed", fontSize: 16, fontWeight: 800 }}>Members who need attention</strong>
          </div>
          <div style={{ marginTop: 8, display: "grid", gap: 6 }}>
            {data.at_risk.map((m) => (
              <div key={m.user_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: 10, background: "white", borderRadius: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--bg-inset, #D8D6D1)", display: "grid", placeItems: "center", fontWeight: 800 }}>
                  {(m.name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{m.name || "Member"}</div>
                  <div style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)" }}>
                    {m.days_since_checkin || "—"}d absent · L{m.risk_level}
                  </div>
                </div>
                <button onClick={() => sendWA(m)} data-testid={`whatsapp-${m.user_id}`}
                  style={{ height: 32, padding: "0 10px", background: "#25D366", color: "white", border: "none", borderRadius: 100, fontSize: 11, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <MessageCircle size={11} /> WhatsApp
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <h3 style={{ fontFamily: "Barlow Condensed", fontSize: 18, fontWeight: 800, marginBottom: 8 }}>Recent check-ins</h3>
        {data.recent_checkins?.length === 0 ? (
          <p style={{ fontSize: 13, color: "var(--text-tertiary, #8A8884)" }}>No check-ins today yet.</p>
        ) : (
          <div style={{ display: "grid", gap: 6 }}>
            {data.recent_checkins.map((c, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: 10, background: "var(--bg-surface, #E4E2DE)", border: "1px solid var(--border, rgba(12,12,11,0.10))", borderRadius: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(245,197,24,0.18)", display: "grid", placeItems: "center", fontSize: 12, fontWeight: 700 }}>
                  {(c.user_name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{c.user_name || "Member"}</div>
                  <div style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)" }}>{new Date(c.checked_in_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false })}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        <QuickAction icon={<Trophy size={18} />} label="Challenge" onClick={() => document.getElementById("create-challenge-btn")?.click()} testid="qa-challenge" />
        <QuickAction icon={<TrendingUp size={18} />} label="Programme" onClick={() => toast("Programmes land in Phase 2")} testid="qa-programme" />
        <QuickAction icon={<Plus size={18} />} label="Add Trainer" onClick={() => toast("Use Trainers tab below")} testid="qa-add-trainer" />
      </section>

      <ChallengesSection gymId={data.gym?.gym_id} />
    </div>
  );
}

function Tile({ label, value, accent, danger }) {
  return (
    <div style={{
      padding: 14,
      background: danger ? "rgba(239,68,68,0.06)" : "var(--bg-surface, #E4E2DE)",
      border: "1px solid",
      borderColor: danger ? "rgba(239,68,68,0.30)" : accent ? "rgba(245,197,24,0.30)" : "var(--border, rgba(12,12,11,0.10))",
      borderRadius: 12,
    }}>
      <div style={{ fontFamily: "Barlow", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-tertiary, #8A8884)" }}>{label}</div>
      <div style={{ fontFamily: "Barlow Condensed", fontSize: 28, fontWeight: 900, lineHeight: 1, color: danger ? "#EF4444" : "var(--text-primary, #0C0C0B)", marginTop: 4 }}>{value}</div>
    </div>
  );
}

function QuickAction({ icon, label, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      style={{ padding: "12px 4px", background: "var(--bg-surface, #E4E2DE)", border: "1px solid var(--border, rgba(12,12,11,0.10))", borderRadius: 12, display: "grid", placeItems: "center", gap: 4, fontSize: 11, fontWeight: 700 }}>
      {icon}
      {label}
    </button>
  );
}

function ChallengesSection({ gymId }) {
  const [data, setData] = useState({ challenges: [] });
  const [open, setOpen] = useState(false);
  const load = () => apiClient.get("/arena/challenges").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  return (
    <section data-testid="challenges-section">
      <div className="flex items-center justify-between" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ fontFamily: "Barlow Condensed", fontSize: 18, fontWeight: 800 }}>Challenges</h3>
        <button id="create-challenge-btn" onClick={() => setOpen(true)} data-testid="open-create-challenge"
          style={{ padding: "6px 12px", background: "#0C0C0B", color: "white", border: "none", borderRadius: 100, fontSize: 11, fontWeight: 700 }}>
          <Plus size={11} style={{ display: "inline", verticalAlign: "middle" }} /> New
        </button>
      </div>
      {data.challenges.length === 0 ? (
        <p style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)", marginTop: 6 }}>No active challenges</p>
      ) : (
        <div style={{ marginTop: 8, display: "grid", gap: 8 }}>
          {data.challenges.map((c) => (
            <div key={c.challenge_id} style={{ padding: 12, background: "var(--bg-surface, #E4E2DE)", border: "1px solid var(--border, rgba(12,12,11,0.10))", borderRadius: 12 }}>
              <div style={{ fontFamily: "Barlow Condensed", fontSize: 16, fontWeight: 800 }}>{c.title}</div>
              <div style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)" }}>{c.metric_type} · {c.status}</div>
              {c.leaderboard?.length > 0 && (
                <div style={{ marginTop: 6, fontSize: 12 }}>
                  Leading: <strong>{c.leaderboard[0].name || "—"}</strong> · {c.leaderboard[0].score || c.leaderboard[0].count}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {open && <CreateChallengeModal onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} />}
    </section>
  );
}

function CreateChallengeModal({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [metric, setMetric] = useState("most_checkins");
  const [duration, setDuration] = useState(7);
  const [prize, setPrize] = useState("");
  const submit = async () => {
    if (!title) return toast.error("Title required");
    try {
      await apiClient.post("/arena/challenges", { title, metric_type: metric, duration_days: duration, prize_description: prize || null });
      toast.success("Challenge launched!");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="fixed inset-0 z-[80] grid place-items-end bg-black/50" onClick={onClose} data-testid="create-challenge-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full bg-white rounded-t-[20px] p-6">
        <h3 style={{ fontFamily: "Barlow Condensed", fontSize: 22, fontWeight: 800 }}>New Arena Challenge</h3>
        <div style={{ display: "grid", gap: 10, marginTop: 14 }}>
          <input value={title} onChange={(e) => setTitle(e.target.value.slice(0, 100))} placeholder="Challenge title" data-testid="ch-title"
            style={{ height: 44, padding: "0 14px", background: "var(--bg-inset, #D8D6D1)", border: "none", borderRadius: 12, fontFamily: "Barlow", fontSize: 14, outline: "none" }} />
          <select value={metric} onChange={(e) => setMetric(e.target.value)} data-testid="ch-metric"
            style={{ height: 44, padding: "0 14px", background: "var(--bg-inset, #D8D6D1)", border: "none", borderRadius: 12, fontFamily: "Barlow", fontSize: 14 }}>
            <option value="most_checkins">Most check-ins</option>
            <option value="most_workouts">Most workouts</option>
            <option value="most_reps">Most reps</option>
            <option value="most_fitpoints">Most FitPoints</option>
            <option value="best_streak">Best streak</option>
          </select>
          <select value={duration} onChange={(e) => setDuration(parseInt(e.target.value, 10))} data-testid="ch-duration"
            style={{ height: 44, padding: "0 14px", background: "var(--bg-inset, #D8D6D1)", border: "none", borderRadius: 12, fontFamily: "Barlow", fontSize: 14 }}>
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
          </select>
          <input value={prize} onChange={(e) => setPrize(e.target.value.slice(0, 200))} placeholder="Prize (optional)"
            style={{ height: 44, padding: "0 14px", background: "var(--bg-inset, #D8D6D1)", border: "none", borderRadius: 12, fontFamily: "Barlow", fontSize: 14, outline: "none" }} />
          <button onClick={submit} data-testid="ch-launch"
            style={{ height: 48, background: "#F5C518", color: "#0C0C0B", border: "none", borderRadius: 100, fontFamily: "Barlow", fontSize: 14, fontWeight: 800 }}>
            Launch
          </button>
        </div>
      </div>
    </div>
  );
}

function MembersTab() {
  const [filter, setFilter] = useState("all");
  const [members, setMembers] = useState([]);
  const load = useCallback(() => {
    apiClient.get("/arena/dashboard/members", { params: { filter } }).then((r) => setMembers(r.data?.members || []));
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  const FILTERS = ["all", "active", "at_risk", "new", "expired"];
  return (
    <div data-testid="members-tab">
      <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4 }}>
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
            style={{
              padding: "6px 12px", whiteSpace: "nowrap",
              background: filter === f ? "#0C0C0B" : "transparent",
              color: filter === f ? "white" : "var(--text-secondary, #4A4845)",
              border: "1px solid", borderColor: filter === f ? "#0C0C0B" : "var(--border, rgba(12,12,11,0.16))",
              borderRadius: 100, fontFamily: "Barlow", fontSize: 12, fontWeight: 700,
              textTransform: "capitalize",
            }}>{f.replace("_", " ")}</button>
        ))}
      </div>
      <div style={{ marginTop: 12, display: "grid", gap: 6 }}>
        {members.length === 0 ? (
          <p style={{ fontSize: 13, color: "var(--text-tertiary, #8A8884)", textAlign: "center", padding: 30 }}>No members</p>
        ) : members.map((m) => (
          <div key={m.user_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: 12, background: "var(--bg-surface, #E4E2DE)", border: "1px solid var(--border, rgba(12,12,11,0.10))", borderRadius: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--bg-inset, #D8D6D1)", display: "grid", placeItems: "center", fontWeight: 800 }}>
              {(m.name || "?").slice(0, 1).toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{m.name || "Member"}</div>
              <div style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)" }}>
                {m.last_checkin_at ? `Last: ${new Date(m.last_checkin_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}` : "Never checked in"}
              </div>
            </div>
            {m.risk_level > 0 && <span style={{ width: 8, height: 8, borderRadius: "50%", background: m.risk_level === 3 ? "#EF4444" : m.risk_level === 2 ? "#F59E0B" : "#FCD34D" }} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function TrainersTab() {
  return (
    <div data-testid="trainers-tab">
      <p style={{ fontSize: 13, color: "var(--text-tertiary, #8A8884)" }}>Manage Arena Trainers from this gym.</p>
      <p style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)", marginTop: 8 }}>Trainer assignment and add-by-phone flow lands in Phase 2 alongside trainer marketplace integration.</p>
    </div>
  );
}

function AnalyticsTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    apiClient.get("/arena/dashboard/analytics", { params: { days: 30 } }).then((r) => setData(r.data));
  }, []);
  if (!data) return <div>Loading…</div>;
  const max = Math.max(1, ...(data.daily_checkins || []).map((d) => d.count));
  return (
    <div data-testid="analytics-tab" style={{ display: "grid", gap: 16 }}>
      <section>
        <h3 style={{ fontFamily: "Barlow Condensed", fontSize: 18, fontWeight: 800, marginBottom: 8 }}>Daily check-ins (30d)</h3>
        <div style={{ display: "flex", gap: 2, alignItems: "flex-end", height: 120, background: "var(--bg-surface, #E4E2DE)", padding: 10, borderRadius: 12 }}>
          {(data.daily_checkins || []).map((d, i) => (
            <div key={i} style={{ flex: 1, height: `${(d.count / max) * 100}%`, background: "#F5C518", borderRadius: 2, minHeight: 2 }} title={`${d._id}: ${d.count}`} />
          ))}
        </div>
      </section>
      <section>
        <h3 style={{ fontFamily: "Barlow Condensed", fontSize: 18, fontWeight: 800, marginBottom: 8 }}>Peak hours</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(24, 1fr)", gap: 1, height: 32, background: "var(--bg-surface, #E4E2DE)", padding: 4, borderRadius: 8 }}>
          {Array.from({ length: 24 }, (_, h) => {
            const r = (data.peak_hours || []).find((x) => parseInt(x._id, 10) === h);
            const c = r?.count || 0;
            const max2 = Math.max(1, ...(data.peak_hours || []).map((x) => x.count));
            const opacity = c ? 0.15 + (c / max2) * 0.85 : 0.05;
            return <div key={h} style={{ background: `rgba(245,197,24,${opacity})`, borderRadius: 2 }} title={`${h}h: ${c}`} />;
          })}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: "var(--text-tertiary, #8A8884)", marginTop: 4 }}>
          <span>00</span><span>06</span><span>12</span><span>18</span><span>23</span>
        </div>
      </section>
    </div>
  );
}

function SettingsTab() {
  const [data, setData] = useState(null);
  const qrRef = useRef(null);
  useEffect(() => { apiClient.get("/arena/dashboard/overview").then((r) => setData(r.data)); }, []);
  if (!data?.gym) return <div>Loading…</div>;
  const tvUrl = `${window.location.origin}/arena/${data.gym.gym_code}/board`;
  const checkInUrl = `${window.location.origin}/checkin/${data.gym.gym_id}/${data.gym.checkin_qr_token || "TOKEN"}`;

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `${data.gym.gym_code}-checkin-qr.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };
  const printPoster = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`
      <html><head><title>${data.gym.gym_name} — Check-in Poster</title>
      <style>
        @page { size: A4; margin: 16mm; }
        body { font-family: 'Barlow', sans-serif; text-align: center; padding: 24px; color: #0C0C0B; }
        h1 { font-size: 42px; margin: 4px 0 0; letter-spacing: -0.02em; }
        h2 { font-size: 18px; font-weight: 700; color: #A37B00; letter-spacing: 0.18em; margin: 0; text-transform: uppercase; }
        .frame { display: inline-block; padding: 24px; border: 4px solid #F5C518; border-radius: 24px; background: white; margin: 32px 0; box-shadow: 0 8px 30px rgba(0,0,0,0.10); }
        img { width: 360px; height: 360px; }
        .code { font-family: monospace; font-size: 28px; letter-spacing: 0.4em; margin-top: 18px; }
        ol { text-align: left; max-width: 480px; margin: 24px auto; padding-left: 24px; line-height: 1.8; font-size: 16px; }
        .footer { margin-top: 30px; font-size: 12px; color: #888; }
      </style></head><body>
      <h2>FitFrik Arena</h2>
      <h1>${data.gym.gym_name}</h1>
      <div class="frame"><img src="${dataUrl}" /></div>
      <div class="code">${data.gym.gym_code}</div>
      <ol>
        <li>Open the FitFrik app on your phone</li>
        <li>Tap the <strong>QR icon</strong> at the top-right of Home</li>
        <li>Point your camera at this QR code</li>
        <li>Get your daily streak boost &amp; +10 FitPoints</li>
      </ol>
      <div class="footer">fitfrik.com · #${data.gym.gym_code}</div>
      </body></html>
    `);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 500);
  };

  return (
    <div data-testid="settings-tab" style={{ display: "grid", gap: 14 }}>
      <div ref={qrRef} style={{ padding: 20, background: "white", border: "1px solid var(--border, rgba(12,12,11,0.10))", borderRadius: 16, textAlign: "center" }} data-testid="qr-poster-block">
        <div style={{ fontSize: 11, fontWeight: 700, color: "#A37B00", textTransform: "uppercase", letterSpacing: "0.10em" }}>Your check-in QR</div>
        <div style={{ fontFamily: "Barlow Condensed", fontSize: 22, fontWeight: 800, marginTop: 4 }}>{data.gym.gym_name}</div>
        <div style={{ display: "inline-block", padding: 14, marginTop: 10, border: "3px solid #F5C518", borderRadius: 16 }}>
          <QRCodeCanvas value={checkInUrl} size={196} level="H" includeMargin={false} />
        </div>
        <div style={{ fontFamily: "monospace", fontSize: 14, letterSpacing: "0.30em", marginTop: 12, color: "#0C0C0B" }}>{data.gym.gym_code}</div>
        <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "center" }}>
          <button onClick={printPoster} data-testid="print-qr"
            style={{ padding: "8px 14px", background: "#0C0C0B", color: "white", border: "none", borderRadius: 100, fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Printer size={12} /> Print A4 poster
          </button>
          <button onClick={downloadQR} data-testid="download-qr"
            style={{ padding: "8px 14px", background: "transparent", color: "#0C0C0B", border: "1px solid var(--border, rgba(12,12,11,0.16))", borderRadius: 100, fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Download size={12} /> PNG
          </button>
        </div>
      </div>

      <Card label="Arena Board TV (display this on your gym TV)" value={tvUrl} testid="tv-url" />
      <Card label="Check-in URL (encoded inside QR above)" value={checkInUrl} testid="checkin-url" />
      <p style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)" }}>
        Print the poster on A4 paper and stick it at your entrance. Members tap the QR icon in their FitFrik app, point at the poster, and get their daily check-in in 3 seconds.
      </p>
    </div>
  );
}

function Card({ label, value, testid }) {
  return (
    <div style={{ padding: 12, background: "var(--bg-surface, #E4E2DE)", border: "1px solid var(--border, rgba(12,12,11,0.10))", borderRadius: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-tertiary, #8A8884)", textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</div>
      <div style={{ fontFamily: "monospace", fontSize: 11, color: "var(--text-primary, #0C0C0B)", marginTop: 4, wordBreak: "break-all" }} data-testid={testid}>{value}</div>
      <button onClick={() => { navigator.clipboard.writeText(value); toast.success("Copied"); }} data-testid={`copy-${testid}`}
        style={{ marginTop: 6, padding: "4px 10px", background: "transparent", color: "#0C0C0B", border: "1px solid var(--border, rgba(12,12,11,0.16))", borderRadius: 100, fontSize: 11, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
        <Copy size={11} /> Copy
      </button>
    </div>
  );
}
